import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Box from '@mui/material/Box';

import { DATA_URLS, EMPTY_GEOJSON, dotsToGeoJson, loadDots } from '../data/census';
import { MIGRATION_COLORS } from '../theme';
import type { CountyProperties } from '../types';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const MAP_STYLE = import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/light-v11';

export const VIEWS = {
  'Lower 48': { center: [-98.5, 39.4] as [number, number], zoom: 3.5 },
  Alaska: { center: [-152, 63.5] as [number, number], zoom: 3.2 },
  Hawaii: { center: [-157.3, 20.6] as [number, number], zoom: 5.9 },
} as const;

export type ViewName = keyof typeof VIEWS;

const SOURCES = {
  counties: 'counties',
  states: 'states',
  positive: 'dots-positive',
  negative: 'dots-negative',
} as const;

const LAYERS = {
  countyFill: 'county-hit',
  countyLine: 'county-line',
  stateLine: 'state-line',
  positive: 'dots-positive-layer',
  negative: 'dots-negative-layer',
} as const;

/** Dots stay pinprick-small at national zoom and grow as you zoom in. */
const DOT_RADIUS: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  2,
  0.7,
  4,
  1.2,
  6,
  2,
  8,
  3.2,
  12,
  6,
];

export interface HoveredCounty {
  props: CountyProperties;
  x: number;
  y: number;
}

interface MigrationMapProps {
  year: number;
  view: ViewName;
  onHoverCounty: (county: HoveredCounty | null) => void;
  onError: (message: string) => void;
  onReady: () => void;
}

export default function MigrationMap({
  year,
  view,
  onHoverCounty,
  onError,
  onReady,
}: MigrationMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const hoveredFipsRef = useRef<string | null>(null);
  const [styleReady, setStyleReady] = useState(false);

  // ---------------------------------------------------------------- init ----
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) {
      onError('VITE_MAPBOX_TOKEN is not set. Copy .env.example to .env and add your token.');
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: VIEWS['Lower 48'].center,
      zoom: VIEWS['Lower 48'].zoom,
      minZoom: 2,
      maxZoom: 12,
      attributionControl: true,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-right');

    map.on('load', () => {
      map.addSource(SOURCES.counties, {
        type: 'geojson',
        data: DATA_URLS.counties,
        promoteId: 'fips',
      });
      map.addSource(SOURCES.states, { type: 'geojson', data: DATA_URLS.states });
      map.addSource(SOURCES.positive, { type: 'geojson', data: EMPTY_GEOJSON });
      map.addSource(SOURCES.negative, { type: 'geojson', data: EMPTY_GEOJSON });

      // Invisible-ish fill: gives us hover targets and a subtle highlight.
      map.addLayer({
        id: LAYERS.countyFill,
        type: 'fill',
        source: SOURCES.counties,
        paint: {
          'fill-color': MIGRATION_COLORS.accent,
          'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.12, 0],
        },
      });

      map.addLayer({
        id: LAYERS.countyLine,
        type: 'line',
        source: SOURCES.counties,
        paint: { 'line-color': '#c9d2da', 'line-width': 0.4 },
      });

      map.addLayer({
        id: LAYERS.stateLine,
        type: 'line',
        source: SOURCES.states,
        paint: { 'line-color': '#8c99a5', 'line-width': 0.8 },
      });

      map.addLayer({
        id: LAYERS.negative,
        type: 'circle',
        source: SOURCES.negative,
        paint: {
          'circle-color': MIGRATION_COLORS.negative,
          'circle-radius': DOT_RADIUS,
          'circle-opacity': 0.72,
          'circle-blur': 0.15,
        },
      });

      map.addLayer({
        id: LAYERS.positive,
        type: 'circle',
        source: SOURCES.positive,
        paint: {
          'circle-color': MIGRATION_COLORS.positive,
          'circle-radius': DOT_RADIUS,
          'circle-opacity': 0.72,
          'circle-blur': 0.15,
        },
      });

      setStyleReady(true);
      onReady();
    });

    map.on('error', (event) => {
      const message = event.error?.message ?? 'Mapbox failed to load.';
      onError(message);
    });

    // ------------------------------------------------------------- hover ----
    const clearHover = () => {
      if (hoveredFipsRef.current !== null) {
        map.setFeatureState(
          { source: SOURCES.counties, id: hoveredFipsRef.current },
          { hover: false },
        );
        hoveredFipsRef.current = null;
      }
      onHoverCounty(null);
    };

    map.on('mousemove', LAYERS.countyFill, (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const props = feature.properties as CountyProperties;
      if (hoveredFipsRef.current !== props.fips) {
        if (hoveredFipsRef.current !== null) {
          map.setFeatureState(
            { source: SOURCES.counties, id: hoveredFipsRef.current },
            { hover: false },
          );
        }
        hoveredFipsRef.current = props.fips;
        map.setFeatureState({ source: SOURCES.counties, id: props.fips }, { hover: true });
      }
      onHoverCounty({ props, x: event.point.x, y: event.point.y });
    });

    map.on('mouseleave', LAYERS.countyFill, clearHover);
    map.getCanvas().style.cursor = 'crosshair';

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Handlers are stable refs from the parent; we deliberately init once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------ dot data ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;

    const controller = new AbortController();

    (async () => {
      try {
        const [positive, negative] = await Promise.all([
          loadDots(year, 'pos', controller.signal),
          loadDots(year, 'neg', controller.signal),
        ]);
        if (controller.signal.aborted) return;

        (map.getSource(SOURCES.positive) as mapboxgl.GeoJSONSource | undefined)?.setData(
          dotsToGeoJson(positive),
        );
        (map.getSource(SOURCES.negative) as mapboxgl.GeoJSONSource | undefined)?.setData(
          dotsToGeoJson(negative),
        );
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        onError((err as Error).message);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, styleReady]);

  // ---------------------------------------------------------------- view ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    const target = VIEWS[view];
    map.easeTo({ center: target.center, zoom: target.zoom, duration: 900 });
  }, [view, styleReady]);

  return <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />;
}
